import {
  callGemini,
  parseJSON,
  sanitizeStringArray,
  flattenStepText,
  extractPdfTextWithGemini,
  LANGUAGE_RULE,
  JSON_RULES,
  PlanDraft,
  SingleLearningPlanRequest,
} from "./OnboardingAPI";

// Strips TOC-style lines (heading text + dot leaders + page number) to avoid
// snippet matches landing in the table of contents.
function stripTocLines(text: string): string {
  return text
    .split("\n")
    .filter((line) => !/\.{4,}\s*\d+\s*$/.test(line.trim()))
    .join("\n");
}

class NormalizedText {
  readonly normalized: string;

  private readonly normalizedLower: string;

  private readonly map: number[];

  constructor(original: string) {
    let norm = "";
    const map: number[] = [];
    let prevWasSpace = false;
    for (let i = 0; i < original.length; i++) {
      const ch = original[i];
      if (/\s/.test(ch)) {
        if (!prevWasSpace && norm.length > 0) {
          norm += " ";
          map.push(i);
        }
        prevWasSpace = true;
      } else {
        norm += ch;
        map.push(i);
        prevWasSpace = false;
      }
    }
    if (norm.endsWith(" ")) {
      norm = norm.slice(0, -1);
      map.pop();
    }
    this.normalized = norm;
    this.normalizedLower = norm.toLowerCase();
    this.map = map;
  }

  findOriginalIndex(snippet: string, originalStartIndex: number): number {
    const normSnippet = snippet.replace(/\s+/g, " ").trim();
    if (!normSnippet) return -1;

    let normStart = 0;
    while (
      normStart < this.map.length &&
      this.map[normStart] < originalStartIndex
    ) {
      normStart++;
    }

    const words = normSnippet.split(" ");
    const ladder = Array.from(
      new Set([words.length, 16, 12, 10, 8, 6, 4])
    )
      .filter((n) => n > 0 && n <= words.length)
      .sort((a, b) => b - a);

    for (const n of ladder) {
      const candidate = words.slice(0, n).join(" ");
      const idx = this.normalized.indexOf(candidate, normStart);
      if (idx !== -1) return this.map[idx];
    }
    // Case-insensitive fallback for headings that may differ in capitalization
    const lowerSnippet = normSnippet.toLowerCase();
    for (const n of ladder) {
      const candidate = lowerSnippet
        .split(" ")
        .slice(0, n)
        .join(" ");
      const idx = this.normalizedLower.indexOf(candidate, normStart);
      if (idx !== -1) return this.map[idx];
    }
    return -1;
  }
}

type RawModule = {
  title?: string;
  description?: string;
  headingSnippet?: string;
  startSnippet?: string;
};

type RawChapter = {
  title?: string;
  description?: string;
  headingSnippet?: string;
  startSnippet?: string;
  modules?: RawModule[];
};

type RawPlan = {
  title?: string;
  shortDescription?: string;
  description?: string;
  level?: string;
  tags?: string[];
  chapters?: RawChapter[];
};

type DocSection = {
  chapterIndex: number;
  moduleIndex: number;
  bodySnippet: string;
  // The heading whose position acts as the END boundary of the PREVIOUS section.
  // For the first module of a chapter this is the CHAPTER heading; otherwise the
  // module's own heading.
  boundaryHeadingSnippet: string;
  bodyStart: number;
  headingPos: number;
};

export async function extractLearningPlanPreserveText({
  sources,
  planName,
  authorName,
  communityName,
  additionalGuidance,
}: SingleLearningPlanRequest): Promise<PlanDraft> {
  const pdfFile = sources.uploadedFiles?.find(
    (f) =>
      f.file &&
      (f.mimeType === "application/pdf" || /\.pdf$/i.test(f.name))
  )?.file;
  const textSource =
    sources.uploadedFiles?.find((f) => typeof f.text === "string" && f.text.trim())
      ?.text ||
    sources.pastedContent ||
    "";

  let fullText = "";
  if (pdfFile) {
    fullText = await extractPdfTextWithGemini(pdfFile);
  } else if (textSource) {
    fullText = textSource;
  } else {
    throw new Error(
      "Preserve-text mode needs a PDF upload or text source to extract from."
    );
  }

  fullText = stripTocLines(fullText);
  if (!fullText.trim()) {
    throw new Error("Could not extract any readable text from the source.");
  }

  const context: string[] = [];
  if (planName?.trim()) context.push(`Plan name: ${planName.trim()}`);
  if (authorName?.trim()) context.push(`Author voice: ${authorName.trim()}`);
  if (communityName?.trim()) context.push(`Community: ${communityName.trim()}`);
  if (additionalGuidance?.trim())
    context.push(`Extra guidance: ${additionalGuidance.trim()}`);

  const instruction = `
You are structuring a source document into a learning plan.

You will NOT write the body content. The body text will be sliced from SOURCE TEXT verbatim using anchor snippets you provide. Do not paraphrase, summarize, or invent prose.

HIERARCHY (critical):
- If SOURCE TEXT contains a table of contents, use it as the authoritative chapter/sub-section hierarchy. TOC entries that are visually indented under another entry are sub-sections (modules) of that entry, not separate chapters.
- A "chapter" is a top-level section. A "module" is a sub-section inside a chapter. Never promote a sub-section to its own chapter.
- Example: if the TOC shows "INTRODUCTION" with "How did I gain that extra weight?" indented underneath, then INTRODUCTION is one chapter and "How did I gain that extra weight?" is one of its modules — they are not two separate chapters.
- If a chapter has no sub-sections in the source, return exactly one module that covers the whole chapter (headingSnippet equal to the chapter heading, startSnippet equal to the chapter body).

Mapping:
- Each chapter in the source becomes one entry in "chapters". JSON stores this as "chapters" but downstream maps to a day plan.
- Each module inside a chapter becomes one entry in "modules" on that chapter. JSON stores this as "modules" but downstream maps to a step.
- Follow the source's actual structure exactly. Do not invent extra chapters or merge ones the source separates.

Snippets (critical — must be EXACT character sequences from SOURCE TEXT, copy verbatim including capitalization, punctuation, and special characters):
- For every chapter, return "headingSnippet": the chapter title exactly as it appears in SOURCE TEXT (often ALL CAPS). Copy it character-for-character.
- For every module, return "headingSnippet": the module title exactly as it appears in SOURCE TEXT.
- For every chapter and every module, return "startSnippet": the FIRST 8-12 words of that section's body prose, copied EXACTLY from SOURCE TEXT.
- startSnippet must start where the body begins, NOT at the heading. Do not include the heading text in startSnippet.
- All snippets must appear in SOURCE TEXT in the same order as your chapters and modules list them.
- Do not invent snippets. If you cannot find a section in SOURCE TEXT, omit it.

What you write (this is the ONLY prose you generate — everything else is sliced verbatim by us):
- plan: title, shortDescription (one sentence), description (2-3 sentences), level (Beginner | Intermediate | Advanced), 3-6 tags
- per-chapter: clean human-readable title (e.g. "Introduction" not "INTRODUCTION") and 1-2 sentence description
- per-module: clean title and 1-2 sentence description

Schema:
{
  "plan": {
    "title": "string",
    "shortDescription": "string",
    "description": "string",
    "level": "Beginner",
    "tags": ["string"],
    "chapters": [
      {
        "title": "Human-readable chapter title",
        "description": "1-2 sentence summary",
        "headingSnippet": "Chapter title verbatim from SOURCE TEXT",
        "startSnippet": "First 8-12 words of chapter body, verbatim",
        "modules": [
          {
            "title": "Human-readable module title",
            "description": "1-2 sentence summary",
            "headingSnippet": "Module title verbatim from SOURCE TEXT",
            "startSnippet": "First 8-12 words of module body, verbatim"
          }
        ]
      }
    ]
  }
}

Context:
${context.length ? context.map((line) => `- ${line}`).join("\n") : "- No additional context provided"}

${LANGUAGE_RULE}
${JSON_RULES}`;

  const parts = [
    { text: `SOURCE TEXT (the document to structure):\n\n${fullText}` },
    { text: instruction },
  ];

  const responseText = await callGemini(parts, 8000, false, "application/json", {
    temperature: 0,
    disableFallback: true,
  });
  const parsed = parseJSON<{ plan?: RawPlan } | RawPlan>(responseText);
  const rawPlan: RawPlan =
    (parsed as { plan?: RawPlan })?.plan ?? (parsed as RawPlan);

  const chapters = Array.isArray(rawPlan?.chapters) ? rawPlan.chapters : [];
  if (!chapters.length) {
    throw new Error("AI did not return any chapter structure.");
  }

  const docSections: DocSection[] = [];
  chapters.forEach((chapter, ci) => {
    const modules = Array.isArray(chapter.modules) ? chapter.modules : [];
    const chapterHeading =
      typeof chapter.headingSnippet === "string" && chapter.headingSnippet.trim()
        ? chapter.headingSnippet.trim()
        : typeof chapter.title === "string"
        ? chapter.title.trim()
        : "";

    if (modules.length === 0) {
      docSections.push({
        chapterIndex: ci,
        moduleIndex: 0,
        bodySnippet: (chapter.startSnippet || "").trim(),
        boundaryHeadingSnippet: chapterHeading,
        bodyStart: 0,
        headingPos: 0,
      });
    } else {
      modules.forEach((mod, mi) => {
        const moduleHeading =
          typeof mod.headingSnippet === "string" && mod.headingSnippet.trim()
            ? mod.headingSnippet.trim()
            : typeof mod.title === "string"
            ? mod.title.trim()
            : "";
        docSections.push({
          chapterIndex: ci,
          moduleIndex: mi,
          bodySnippet: (mod.startSnippet || "").trim(),
          // First module of chapter uses the chapter heading as the boundary;
          // subsequent modules use their own heading.
          boundaryHeadingSnippet: mi === 0 ? chapterHeading : moduleHeading,
          bodyStart: 0,
          headingPos: 0,
        });
      });
    }
  });

  const normalized = new NormalizedText(fullText);
  let cursor = 0;
  for (const section of docSections) {
    let headingIdx = section.boundaryHeadingSnippet
      ? normalized.findOriginalIndex(section.boundaryHeadingSnippet, cursor)
      : -1;
    if (headingIdx >= 0) cursor = headingIdx;

    let bodyIdx = section.bodySnippet
      ? normalized.findOriginalIndex(section.bodySnippet, cursor)
      : -1;
    if (bodyIdx < 0) bodyIdx = cursor;

    section.headingPos = headingIdx >= 0 ? headingIdx : bodyIdx;
    section.bodyStart = bodyIdx;
    cursor = bodyIdx + 1;
  }

  const sliceFor = (i: number): string => {
    const start = docSections[i].bodyStart;
    const end =
      i + 1 < docSections.length
        ? docSections[i + 1].headingPos
        : fullText.length;
    return fullText.slice(start, end).trim();
  };

  const dayPlans = chapters.map((chapter, ci) => {
    const modules = Array.isArray(chapter.modules) ? chapter.modules : [];
    const sectionsForChapter = docSections
      .map((section, idx) => ({ section, idx }))
      .filter(({ section }) => section.chapterIndex === ci);

    const steps = sectionsForChapter.map(({ section, idx }, position) => {
      const sourceModule =
        modules[section.moduleIndex] ?? {
          title: chapter.title,
          description: chapter.description,
        };
      const title =
        typeof sourceModule.title === "string" && sourceModule.title.trim()
          ? sourceModule.title.trim()
          : `Module ${position + 1}`;
      const description = flattenStepText(sourceModule.description);
      const text = flattenStepText(sliceFor(idx));

      return {
        title,
        subtitle: "",
        sectionTitle:
          typeof chapter.title === "string" && chapter.title.trim()
            ? chapter.title.trim()
            : "Section",
        description,
        position: position + 1,
        learningStyle: "NARRATIVE" as const,
        ...(text ? { text } : {}),
      };
    });

    return {
      title:
        typeof chapter.title === "string" && chapter.title.trim()
          ? chapter.title.trim()
          : `Chapter ${ci + 1}`,
      description:
        typeof chapter.description === "string" && chapter.description.trim()
          ? chapter.description.trim()
          : "",
      steps,
    };
  });

  return {
    title:
      typeof rawPlan.title === "string" && rawPlan.title.trim()
        ? rawPlan.title.trim()
        : planName?.trim() || "Learning Plan",
    shortDescription:
      typeof rawPlan.shortDescription === "string" && rawPlan.shortDescription.trim()
        ? rawPlan.shortDescription.trim()
        : "",
    description:
      typeof rawPlan.description === "string" && rawPlan.description.trim()
        ? rawPlan.description.trim()
        : "",
    level:
      typeof rawPlan.level === "string" && rawPlan.level.trim()
        ? rawPlan.level.trim()
        : "Beginner",
    tags: sanitizeStringArray(rawPlan.tags),
    dayPlans,
  };
}
